// components/OTPVerification.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MyContext } from "../../../src/App";
import { postData } from "../../utils/api";
import CircularProgress from "@mui/material/CircularProgress";

const OTPVerification = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(300); // 5 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);
  
  const navigate = useNavigate();
  const location = useLocation();
  const context = useContext(MyContext);
  
  // Get user data passed from registration
  const userData = location.state?.userData;
  const userEmail = userData?.email || "";

  useEffect(() => {
    // Redirect if no user data
    if (!userData) {
      navigate("/signup");
      return;
    }

    // Start timer
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Focus first input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    return () => clearInterval(interval);
  }, [userData, navigate]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    const newOtp = [...otp];
    
    for (let i = 0; i < Math.min(pastedData.length, 6); i++) {
      newOtp[i] = pastedData[i];
    }
    
    setOtp(newOtp);
    
    // Focus appropriate input after paste
    const nextEmptyIndex = newOtp.findIndex(digit => digit === "");
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please enter complete 6-digit OTP"
      });
      return;
    }

    setIsVerifying(true);

    try {
      const response = await postData("/api/users/verify-otp", {
        email: userEmail,
        otp: otpString,
        userData: userData
      });

      if (response && !response.error) {
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Registration successful! You can now login."
        });
        
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: response?.msg || "Invalid OTP. Please try again."
        });
        // Clear OTP on error
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Verification failed. Please try again."
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;

    context.setIsLoading(true);

    try {
      const response = await postData("/api/users/resend-otp", {
        email: userEmail,
        name: userData.name
      });

      if (response && !response.error) {
        context.setAlertBox({
          open: true,
          error: false,
          msg: "OTP resent successfully! Please check your email."
        });
        
        // Reset timer and states
        setTimer(300);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: response?.msg || "Failed to resend OTP. Please try again."
        });
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to resend OTP. Please try again."
      });
    } finally {
      context.setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="container d-flex justify-content-center align-items-center vh-100">
      <div className="col-md-6 col-lg-5 shadow p-5 rounded bg-white">
        <div className="text-center mb-4">
          <div className="mb-3">
            <i className="fas fa-envelope-open-text" style={{ fontSize: "60px", color: "#28a745" }}></i>
          </div>
          <h3>Verify Your Email</h3>
          <p className="text-muted">
            We've sent a 6-digit verification code to
          </p>
          <p className="fw-bold">{userEmail}</p>
        </div>

        <form onSubmit={handleVerifyOtp}>
          <div className="d-flex justify-content-center mb-4">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(ref) => (inputRefs.current[index] = ref)}
                type="text"
                maxLength="1"
                className="form-control text-center mx-1"
                style={{
                  width: "50px",
                  height: "50px",
                  fontSize: "20px",
                  fontWeight: "bold"
                }}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isVerifying}
              />
            ))}
          </div>

          <button
            type="submit"
            className="btn btn-success w-100 mb-3"
            disabled={isVerifying || otp.join("").length !== 6}
          >
            {isVerifying ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Verify OTP"
            )}
          </button>
        </form>

        <div className="text-center">
          {timer > 0 ? (
            <p className="text-muted">
              Resend OTP in <span className="fw-bold text-primary">{formatTime(timer)}</span>
            </p>
          ) : (
            <div>
              <p className="text-muted mb-2">Didn't receive the code?</p>
              <button
                className="btn btn-outline-primary"
                onClick={handleResendOtp}
                disabled={context.isLoading}
              >
                {context.isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Resend OTP"
                )}
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-4">
          <button
            className="btn btn-link text-decoration-none"
            onClick={() => navigate("/signup")}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Registration
          </button>
        </div>

        <div className="mt-4 p-3 bg-light rounded">
          <h6 className="mb-2">Tips:</h6>
          <ul className="list-unstyled mb-0 small text-muted">
            <li>• Check your spam/junk folder</li>
            <li>• OTP is valid for 5 minutes</li>
            <li>• You can paste the OTP directly</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;